<template>
  <div class="wa-mention-list" role="listbox" aria-label="Participantes do grupo">
    <div v-if="loading" class="wa-mention-list__loading">
      Carregando participantes...
    </div>

    <template v-else-if="participants.length">
      <button
        v-for="(item, index) in participants"
        :key="item.jid"
        class="wa-mention-list__item"
        :class="{ 'wa-mention-list__item--active': index === selectedIndex }"
        role="option"
        :aria-selected="index === selectedIndex"
        @mousedown.prevent="selectItem(index)"
        @mouseenter="selectedIndex = index"
      >
        <span class="wa-mention-list__avatar">{{ initials(item.name) }}</span>
        <span class="wa-mention-list__info">
          <span class="wa-mention-list__name">{{ item.name }}</span>
          <span class="wa-mention-list__phone">+{{ item.phone }}</span>
        </span>
        <span v-if="item.isAdmin" class="wa-mention-list__badge">admin</span>
      </button>
    </template>

    <div v-else class="wa-mention-list__empty">
      Nenhum participante encontrado
    </div>
  </div>
</template>

<script>
export default {
  name: 'WaMentionList',

  props: {
    searchKey:      { type: String,  default: '' },
    groupJid:       { type: String,  required: true },
    instanceName:   { type: String,  required: true },
    conversationId: { type: Number,  required: true },
  },

  emits: ['select-participant'],

  data() {
    return {
      allParticipants: [],
      loading: false,
      selectedIndex: 0,
    };
  },

  computed: {
    participants() {
      if (!this.searchKey) return this.allParticipants;
      const q = this.searchKey.toLowerCase();
      return this.allParticipants.filter(
        p => p.name.toLowerCase().includes(q) || p.phone.includes(q)
      );
    },
  },

  watch: {
    // Fetch when first shown (groupJid becomes available)
    groupJid: {
      immediate: true,
      handler(val) {
        if (val) this.fetchParticipants();
      },
    },
    participants() {
      this.selectedIndex = 0;
    },
  },

  mounted() {
    // Keyboard navigation — the ProseMirror suggestionsPlugin calls
    // onKeyDown on the component's root element. We intercept via keydown
    // on the document during the lifecycle of this component.
    this._keydownHandler = this.onKeyDown.bind(this);
    document.addEventListener('keydown', this._keydownHandler, true);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this._keydownHandler, true);
  },

  methods: {
    async fetchParticipants() {
      if (!this.groupJid || !this.instanceName) return;
      this.loading = true;
      try {
        const base = window.MENTION_BRIDGE_URL || '/mention-bridge';
        const params = new URLSearchParams({
          instanceName: this.instanceName,
          groupJid:     this.groupJid,
          inboxId:      '0',
        });
        const resp = await fetch(
          `${base}/api/participants/${this.conversationId}?${params}`,
          { credentials: 'same-origin' }
        );
        if (resp.ok) {
          const data = await resp.json();
          this.allParticipants = data.participants || [];
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[WaMentionList] fetch error:', e.message);
      } finally {
        this.loading = false;
      }
    },

    selectItem(index) {
      const item = this.participants[index];
      if (item) this.$emit('select-participant', item);
    },

    onKeyDown(event) {
      // Only intercept when this list is visible
      if (event.key === 'ArrowDown') {
        event.stopPropagation();
        event.preventDefault();
        this.selectedIndex =
          (this.selectedIndex + 1) % Math.max(this.participants.length, 1);
        this.scrollActiveIntoView();
      } else if (event.key === 'ArrowUp') {
        event.stopPropagation();
        event.preventDefault();
        this.selectedIndex =
          (this.selectedIndex - 1 + this.participants.length) %
          Math.max(this.participants.length, 1);
        this.scrollActiveIntoView();
      } else if (event.key === 'Enter') {
        event.stopPropagation();
        event.preventDefault();
        this.selectItem(this.selectedIndex);
      }
    },

    initials(name) {
      if (!name) return '?';
      return name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
    },

    scrollActiveIntoView() {
      this.$nextTick(() => {
        this.$el
          ?.querySelector('.wa-mention-list__item--active')
          ?.scrollIntoView({ block: 'nearest' });
      });
    },
  },
};
</script>

<style lang="scss">
.wa-mention-list {
  position: absolute;
  bottom: 100%;
  left: 0;
  z-index: 1000;
  min-width: 240px;
  max-width: 320px;
  max-height: 220px;
  overflow-y: auto;
  background: var(--color-background, #fff);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,.12);
  padding: 4px 0;

  &__loading,
  &__empty {
    padding: 8px 12px;
    color: var(--color-body, #6b7280);
    font-size: 13px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 12px;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;

    &:hover,
    &--active {
      background: var(--color-background-light, #f3f4f6);
    }
  }

  &__avatar {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #6366f1;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__info {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__name {
    font-size: 13px;
    font-weight: 500;
    color: var(--color-body, #111827);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__phone {
    font-size: 11px;
    color: var(--color-body-light, #9ca3af);
  }

  &__badge {
    flex-shrink: 0;
    font-size: 10px;
    padding: 1px 5px;
    border-radius: 8px;
    background: #ef4444;
    color: #fff;
  }
}
</style>
